import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Plus, Trash2, History, TrendingUp, LineChart } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["Not started", "On track", "At risk", "Behind", "Realised"];
const CREDIBILITY = ["indicative", "validated", "committed"];
const credCls = (c) => c === "committed" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : c === "validated" ? "text-sky-700 bg-sky-50 border-sky-200"
  : "text-zinc-500 bg-zinc-50 border-zinc-200";
const statusCls = (st) => st === "Realised" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : st === "On track" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : st === "At risk" ? "text-amber-700 bg-amber-50 border-amber-200"
  : st === "Behind" ? "text-red-700 bg-red-50 border-red-200"
  : "text-zinc-500 bg-zinc-50 border-zinc-200";
const inputCls = "border border-zinc-300 rounded px-2 py-1 text-[12px] bg-white";

function ActualForm({ onAdd, busy }) {
  const [f, setF] = useState({ period: "", value: "", note: "" });
  const submit = () => {
    if (!f.value.trim() && !f.period.trim()) { toast.error("Enter a period and value"); return; }
    onAdd(f); setF({ period: "", value: "", note: "" });
  };
  return (
    <div className="flex items-end gap-2 flex-wrap">
      <input className={`${inputCls} w-24`} placeholder="Period (Q1)" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} />
      <input className={`${inputCls} w-28`} placeholder="Actual value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />
      <input className={`${inputCls} flex-1 min-w-[120px]`} placeholder="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      <button disabled={busy} onClick={submit}
        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase bg-[var(--e360-primary)] text-white px-3 py-1.5 rounded disabled:opacity-40">
        <TrendingUp size={13} /> Log actual</button>
    </div>
  );
}

function BenefitRow({ item, onSave, onDelete, busy }) {
  const [expand, setExpand] = useState(false);
  const patch = (body) => onSave(item.id, body);
  return (
    <div data-testid={`benefit-row-${item.id}`} className="px-3 py-2.5">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="text-[11px] text-zinc-400 font-mono w-16 pt-1 flex-none">{item.ref}</span>
        <div className="flex-1 min-w-[240px]">
          <button onClick={() => setExpand((v) => !v)} className="text-left" data-testid={`benefit-title-${item.id}`}>
            <div className="text-[13px] text-zinc-800 font-medium">{item.benefit}</div>
          </button>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            Owner: {item.owner} · Baseline: {item.baseline} · Target: {item.target}
            {item.latest != null && item.latest !== "" && <span className="text-[var(--e360-primary)] font-medium"> · Latest: {item.latest}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select data-testid={`benefit-status-${item.id}`} className={`${inputCls} font-semibold ${statusCls(item.status)}`} value={item.status} disabled={busy}
            onChange={(e) => patch({ status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid={`benefit-credibility-${item.id}`} title="Benefit credibility (CFO ladder)"
            className={`${inputCls} font-semibold ${credCls(item.credibility)}`} value={item.credibility || "indicative"} disabled={busy}
            onChange={(e) => patch({ credibility: e.target.value })}>
            {CREDIBILITY.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
          <input data-testid={`benefit-owner-${item.id}`} className={`${inputCls} w-28`} defaultValue={item.owner} disabled={busy}
            onBlur={(e) => e.target.value !== item.owner && patch({ owner: e.target.value })} title="Owner" />
          <button data-testid={`benefit-expand-${item.id}`} onClick={() => setExpand((v) => !v)} title="Actuals & history"
            className="text-zinc-400 hover:text-black p-1"><LineChart size={14} /></button>
          <button data-testid={`benefit-delete-${item.id}`} onClick={() => onDelete(item)} disabled={busy}
            className="text-zinc-300 hover:text-red-600 p-1" title={item.seed_ref ? "Hide (seeded)" : "Delete"}><Trash2 size={14} /></button>
        </div>
      </div>
      {expand && (
        <div data-testid={`benefit-detail-${item.id}`} className="mt-2 ml-16 border-l-2 border-zinc-200 pl-3 space-y-2">
          <div className="text-[11px] text-zinc-500">Measure: {item.measure} · Cadence: {item.cadence}</div>
          {(item.actuals || []).length > 0 && (
            <div data-testid={`benefit-actuals-${item.id}`} className="flex flex-wrap gap-1.5">
              {item.actuals.map((a, i) => (
                <span key={i} className="text-[11px] bg-zinc-100 text-zinc-700 rounded-full px-2.5 py-0.5" title={`${a.note || ""} ${a.by || ""}`}>
                  <b>{a.period}</b>: {a.value}
                </span>
              ))}
            </div>
          )}
          <ActualForm onAdd={(a) => patch({ add_actual: a })} busy={busy} />
          {(item.updates || []).length > 0 && (
            <div className="space-y-1 pt-1">
              {item.updates.slice(-6).map((u, i) => (
                <div key={i} className="text-[11px] text-zinc-500">
                  <span className="text-zinc-400">{new Date(u.at).toLocaleDateString("en-GB")}</span>
                  {u.from_status && <> · {u.from_status} → <b>{u.status}</b></>}
                  {u.note && <> · {u.note}</>}{u.by && <span className="text-zinc-300"> · {u.by}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddBenefitForm({ onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ benefit: "", owner: "", baseline: "", target: "", measure: "", cadence: "" });
  const submit = async () => {
    if (!f.benefit.trim()) { toast.error("Enter a benefit"); return; }
    await onAdd(f); setF({ benefit: "", owner: "", baseline: "", target: "", measure: "", cadence: "" }); setOpen(false);
  };
  if (!open) return (
    <button data-testid="benefit-add-toggle" onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5">
      <Plus size={14} /> Add benefit
    </button>
  );
  return (
    <div data-testid="benefit-add-form" className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 space-y-2">
      <input data-testid="benefit-add-benefit" className={`${inputCls} w-full`} placeholder="Benefit" value={f.benefit} onChange={(e) => setF({ ...f, benefit: e.target.value })} />
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid="benefit-add-owner" className={inputCls} placeholder="Owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        <input data-testid="benefit-add-baseline" className={inputCls} placeholder="Baseline" value={f.baseline} onChange={(e) => setF({ ...f, baseline: e.target.value })} />
        <input data-testid="benefit-add-target" className={inputCls} placeholder="Target" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} />
        <input data-testid="benefit-add-measure" className={`${inputCls} sm:col-span-2`} placeholder="Measure" value={f.measure} onChange={(e) => setF({ ...f, measure: e.target.value })} />
        <input data-testid="benefit-add-cadence" className={inputCls} placeholder="Cadence" value={f.cadence} onChange={(e) => setF({ ...f, cadence: e.target.value })} />
      </div>
      <div className="flex gap-2">
        <button data-testid="benefit-add-submit" onClick={submit} disabled={busy}
          className="text-[12px] tracking-[0.12em] uppercase bg-[var(--e360-primary)] text-white px-4 py-1.5 rounded disabled:opacity-40">Add</button>
        <button onClick={() => setOpen(false)} className="text-[12px] tracking-[0.12em] uppercase text-zinc-500 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center"><div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div></div>
);

export default function BenefitsTracker({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/benefits`); setData(data); }
    catch { toast.error("Could not load the benefits tracker"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const withBusy = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setData(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const sync = () => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/benefits/seed`), "Synced from blueprint");
  const add = (body) => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/benefits`, body), "Benefit added");
  const save = (id, body) => withBusy(() => api.put(`/admin/enterprise360/clients/${clientId}/benefits/${id}`, body));
  const del = (item) => { if (!window.confirm(item.seed_ref ? "Hide this seeded benefit?" : "Delete this benefit?")) return;
    withBusy(() => api.delete(`/admin/enterprise360/clients/${clientId}/benefits/${item.id}`), "Removed"); };

  const s = data?.summary || {};
  const items = data?.items || [];
  const overallCls = s.overall === "red" ? "text-red-700 bg-red-50 border-red-200" : s.overall === "amber" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200";
  return (
    <div data-testid="benefits-tracker">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-[12px] text-zinc-500 max-w-xl">Track live actuals each cadence against baseline and target — mark each benefit on-track, at-risk, behind or realised.</p>
        <div className="flex items-center gap-2">
          {data?.seed_available > 0 && <span data-testid="benefit-seed-available" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">{data.seed_available} new from blueprint</span>}
          <button data-testid="benefit-sync-btn" onClick={sync} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5 disabled:opacity-40">
            <RefreshCw size={13} /> Sync from blueprint</button>
        </div>
      </div>
      <div data-testid="benefit-summary" className="flex items-center gap-6 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
        <span data-testid="benefit-overall" className={`text-[12px] rounded-full px-3 py-1 border font-semibold ${overallCls}`}>
          ● {s.overall === "green" ? "On track" : s.overall === "amber" ? "Watch" : "Behind"}
        </span>
        <Stat label="Realised" value={s.realised ?? 0} cls="text-emerald-600" />
        <Stat label="At risk" value={s.at_risk ?? 0} cls={s.at_risk ? "text-amber-600" : "text-black"} />
        <Stat label="Behind" value={s.behind ?? 0} cls={s.behind ? "text-red-600" : "text-black"} />
        <Stat label="Tracking" value={s.tracking ?? 0} />
        <Stat label="Committed" value={s.committed ?? 0} cls={s.committed ? "text-emerald-600" : "text-zinc-400"} />
        <Stat label="Total" value={s.total ?? 0} cls="text-zinc-400" />
      </div>
      <div className="mb-3"><AddBenefitForm onAdd={add} busy={busy} /></div>
      {loading ? <div className="text-sm text-zinc-400 py-6 text-center">Loading benefits…</div>
        : items.length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">No benefits yet. Sync from the blueprint or add one.</div>
        : <div className="border border-zinc-200 rounded-lg divide-y" data-testid="benefit-list">
            {items.map((it) => <BenefitRow key={it.id} item={it} onSave={save} onDelete={del} busy={busy} />)}
          </div>}
    </div>
  );
}
